// src/features/chat/ResolveChatContextPanel.tsx
//
// Resolve the effective ChatPolicy for a scope entity by merging its policy
// chain (chat.ResolveChatContext) — same "context resolution" tool pattern
// as /admin/context's LayoutResolver, applied to chat policy inheritance
// instead of Context Panel layout.

import { useState } from 'react';
import { chatApi } from '@core/api';
import { useI18n } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import type { ResolveChatContextResponse } from '@core/types/chat';

export function ResolveChatContextPanel() {
  const { t } = useI18n();
  const [entityType, setEntityType] = useState('team');
  const [entityId, setEntityId] = useState('');
  const [result, setResult] = useState<ResolveChatContextResponse | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityId.trim()) return;
    setIsResolving(true);
    setError(null);
    try {
      const res = await chatApi.ResolveChatContext({
        scope_entity: { type_id: '', type_name: entityType.trim(), id: entityId.trim(), platform_id: '', tenant_id: '', product_id: '' },
        scope_chain: [],
      });
      setResult(res);
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('chat.resolve.subtitle')}</p>

      <form onSubmit={handleResolve} className="flex flex-wrap items-end gap-3">
        <Input label={t('chat.policies.scopeEntityType')} value={entityType} onChange={e => setEntityType(e.target.value)} className="w-40" />
        <Input label={t('chat.policies.scopeEntityId')} value={entityId} onChange={e => setEntityId(e.target.value)} className="w-64" required />
        <Button type="submit" size="sm" isLoading={isResolving}>{t('chat.resolve.resolve')}</Button>
      </form>

      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {result && (
        <div className="space-y-3">
          {result.effective_policy ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <h4 className="text-sm font-semibold text-foreground">{t('chat.resolve.effectivePolicy')}</h4>
              <p className="mt-1 text-sm text-foreground">{result.effective_policy.name}</p>
              {result.effective_policy.description && (
                <p className="text-xs text-muted-foreground">{result.effective_policy.description}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('chat.resolve.noPolicy')}</p>
          )}

          {result.policy_chain.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('chat.resolve.chain')}
              </h4>
              <ol className="space-y-1.5">
                {result.policy_chain.map((p, idx) => (
                  <li key={p.entity?.id ?? idx} className="rounded-md border border-border px-3 py-1.5 text-sm">
                    <span className="mr-2 text-xs text-muted-foreground">{idx + 1}.</span>
                    {p.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({p.scope_entity_type || p.scope_entity?.type_name})
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ResolveChatContextPanel;
